import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { SheetHandle, sheetSafeArea, sheetSurface } from '@/components/ui/Sheet'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  /** Id of a heading inside `children` that names the dialog, when there is no `title`. */
  labelledBy?: string
  /** `lg` fits wide content such as paradigm tables. */
  size?: 'md' | 'lg'
  children: ReactNode
}

export function Modal({ open, onClose, title, labelledBy, size = 'md', children }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return

    // Restore rather than clear — a modal opened from another modal must not
    // hand scrolling back to the page while the outer one is still up.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()

    return () => { document.body.style.overflow = previousOverflow }
  }, [open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : labelledBy}
    >
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          'relative w-full max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl border border-border-divider bg-card shadow-xl outline-none',
          sheetSurface,
          'max-sm:max-h-[88dvh]',
          size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-md',
        )}
      >
        <SheetHandle />
        {title && (
          <div className="px-6 pt-6 pb-2 max-sm:pt-3">
            <h2 id="modal-title" className="font-serif text-lg font-medium text-heading">
              {title}
            </h2>
          </div>
        )}
        <div className={cn(title ? 'px-6 pb-6' : 'p-6', 'max-sm:px-5', sheetSafeArea)}>
          {children}
        </div>
      </div>
    </div>
  )
}
